/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

// Launch step 2 - pick a saved template, a Podstack catalog image, or bring
// your own container image (optionally from a private registry).

import React, { useMemo, useState } from 'react';
import { Chip, Skeleton, Tag } from '../../console/ui';
import { SearchIc } from '../../console/icons';
import { LaunchTemplate } from '../../../types';
import {
  CATEGORY_LABEL,
  TEMPLATE_TABS,
  TemplateTab,
  getTemplateCategory,
  isPopularTemplate,
  shortImage,
  templatePortHint,
} from './catalog';

export const CUSTOM_IMAGE = '__custom__';

interface RegistryFields {
  image: string;
  is_private_registry: boolean;
  registry_url: string;
  registry_username: string;
  registry_password: string;
}

interface TemplateStepProps {
  myTemplates: LaunchTemplate[];
  catalog: LaunchTemplate[];
  loading: boolean;
  pickedId: string | null;
  onPick: (template: LaunchTemplate | null) => void;
  form: RegistryFields;
  onFormChange: (patch: Partial<RegistryFields>) => void;
}

const pickCls = (on: boolean) =>
  `text-left ps-card p-4 flex flex-col gap-2 min-h-[44px] transition-colors hover:border-ps-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-ps-accent ${
    on ? '!border-ps-accent bg-ps-accent-soft' : ''
  }`;

const TemplateStep: React.FC<TemplateStepProps> = ({
  myTemplates,
  catalog,
  loading,
  pickedId,
  onPick,
  form,
  onFormChange,
}) => {
  const [query, setQuery] = useState('');
  const [tab, setTab] = useState<TemplateTab>('all');

  const withMeta = useMemo(
    () => catalog.map((t) => ({ t, category: getTemplateCategory(t), popular: isPopularTemplate(t) })),
    [catalog],
  );
  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    TEMPLATE_TABS.forEach(({ key }) => {
      c[key] =
        key === 'all'
          ? withMeta.length
          : key === 'popular'
            ? withMeta.filter((m) => m.popular).length
            : withMeta.filter((m) => m.category === key).length;
    });
    return c;
  }, [withMeta]);

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return withMeta
      .filter(({ t }) =>
        !q ||
        t.name.toLowerCase().includes(q) ||
        (t.description || '').toLowerCase().includes(q) ||
        t.image_name.toLowerCase().includes(q) ||
        (t.gpu_type || '').toLowerCase().includes(q) ||
        (t.tags || []).some((tag) => tag.toLowerCase().includes(q)),
      )
      .filter(({ category, popular }) => (tab === 'all' ? true : tab === 'popular' ? popular : category === tab))
      // Popular first, keep catalog order otherwise
      .sort((a, b) => Number(b.popular) - Number(a.popular));
  }, [withMeta, query, tab]);

  return (
    <div className="flex flex-col gap-6">
      {myTemplates.length > 0 && (
        <section className="flex flex-col gap-3" aria-labelledby="tpl-mine">
          <h2 id="tpl-mine" className="m-0 text-base font-medium">
            Your templates
          </h2>
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))]">
            {myTemplates.map((t) => (
              <button key={t.id} type="button" className={pickCls(pickedId === t.id)} onClick={() => onPick(t)} aria-pressed={pickedId === t.id}>
                <div className="flex justify-between gap-2 w-full">
                  <span className="font-semibold truncate">{t.name}</span>
                  <Tag tone="info">Saved</Tag>
                </div>
                <span className="text-[13px] text-ps-muted">
                  {[shortImage(t.image_name), t.cpu_cores ? `${t.cpu_cores} vCPU` : '', t.memory_gb ? `${t.memory_gb} GB` : '', t.gpu_type || '']
                    .filter(Boolean)
                    .join(' · ')}
                </span>
              </button>
            ))}
          </div>
        </section>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="tpl-catalog">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="tpl-catalog" className="m-0 text-base font-medium">
            Podstack images <span className="text-ps-subtle text-sm font-normal">{catalog.length}</span>
          </h2>
          <label className="relative w-full sm:w-72">
            <span className="sr-only">Search templates</span>
            <SearchIc className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-ps-subtle" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Try “llama”, “jupyter”, “comfyui”…"
              className="ps-input pl-9"
            />
          </label>
        </div>
        <div className="flex flex-wrap gap-2">
          {TEMPLATE_TABS.map(({ key, label }) => (
            <Chip key={key} on={tab === key} onClick={() => setTab(key)}>
              {label} <span className="text-ps-subtle">{counts[key] ?? 0}</span>
            </Chip>
          ))}
        </div>
        {loading ? (
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))]">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-[112px]" />
            ))}
          </div>
        ) : visible.length === 0 ? (
          <div className="ps-card p-6 text-center text-ps-muted text-sm">
            {query || tab !== 'all' ? (
              <>
                No templates match.{' '}
                <button
                  type="button"
                  className="ps-link"
                  onClick={() => {
                    setQuery('');
                    setTab('all');
                  }}
                >
                  Clear filters
                </button>
              </>
            ) : (
              'No catalog templates available right now - use your own image below.'
            )}
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-[repeat(auto-fill,minmax(min(100%,240px),1fr))]">
            {visible.map(({ t, category, popular }) => (
              <button key={t.id} type="button" className={pickCls(pickedId === t.id)} onClick={() => onPick(t)} aria-pressed={pickedId === t.id}>
                <div className="flex justify-between gap-2 w-full">
                  <span className="font-semibold truncate">{t.name}</span>
                  <span className="flex gap-1 flex-none">
                    {popular && <Tag tone="accent">Popular</Tag>}
                    <Tag tone="off">{t.category?.name || CATEGORY_LABEL[category]}</Tag>
                  </span>
                </div>
                {t.description && <span className="text-[13px] leading-[1.45] text-ps-muted line-clamp-2">{t.description}</span>}
                {templatePortHint(t) && <span className="font-mono text-xs text-ps-subtle">{templatePortHint(t)}</span>}
              </button>
            ))}
          </div>
        )}
      </section>

      <section
        className={`ps-card p-[18px] flex flex-col gap-3 ${pickedId === CUSTOM_IMAGE ? '!border-ps-accent' : ''}`}
        aria-labelledby="tpl-own"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h2 id="tpl-own" className="m-0 text-base font-medium">
            Or use your own image
          </h2>
          {pickedId !== CUSTOM_IMAGE && (
            <button type="button" className="ps-btn2 ps-btn-sm" onClick={() => onPick(null)}>
              Use custom image
            </button>
          )}
        </div>
        <label className="block">
          <span className="ps-label">Container image</span>
          <input
            type="text"
            className="ps-input font-mono"
            placeholder="registry/org/image:tag"
            value={form.image}
            onFocus={() => pickedId !== CUSTOM_IMAGE && onPick(null)}
            onChange={(e) => onFormChange({ image: e.target.value })}
          />
        </label>
        <label className="flex items-center gap-2.5 min-h-[44px] text-sm text-ps-muted cursor-pointer">
          <input
            type="checkbox"
            id="private-registry"
            checked={form.is_private_registry}
            onChange={(e) => onFormChange({ is_private_registry: e.target.checked })}
            className="w-[18px] h-[18px] accent-[#F97316]"
          />
          Private registry (requires authentication)
        </label>
        {form.is_private_registry && (
          <div className="grid gap-3 sm:grid-cols-3">
            <label className="block">
              <span className="ps-label">Registry URL</span>
              <input
                type="text"
                className="ps-input"
                placeholder="registry.example.com"
                value={form.registry_url}
                onChange={(e) => onFormChange({ registry_url: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="ps-label">Username</span>
              <input
                type="text"
                className="ps-input"
                placeholder="username"
                value={form.registry_username}
                onChange={(e) => onFormChange({ registry_username: e.target.value })}
              />
            </label>
            <label className="block">
              <span className="ps-label">Password / token</span>
              <input
                type="password"
                className="ps-input"
                placeholder="password or token"
                value={form.registry_password}
                onChange={(e) => onFormChange({ registry_password: e.target.value })}
              />
            </label>
          </div>
        )}
        <p className="m-0 text-xs text-ps-subtle">Exposed ports are set on the next step.</p>
      </section>
    </div>
  );
};

export default TemplateStep;
